import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Box, Button, Grid, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import SendIcon from "@mui/icons-material/Send";
import ReceiptIcon from "@mui/icons-material/Receipt";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import InputFieldText from "Components/Common/InputFieldText";
import BaseTable from "Components/Common/BaseTable";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import DebitNoteForm from "./DebitNoteForm";
import Tooltip from "Components/Common/Tooltip";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import DatePicker from "Components/Common/DatePicker";
import { CONSUMER, POWER_READ_ADMIN } from "common/constant";
import { DEBIT_NOTE } from "tagTypes";

import getFileExtension from "utils/getFileExtension";
import { formatUtilityAddress } from "utils/formatUtilityAddress";
import debounceFn from "utils/debounce";
import { getImageUrl } from "utils";

import {
  useDeleteDebitNoteMutation,
  usePostDebitNoteFileMutation,
  usePostSendDebitNoticeMutation,
} from "apis/UtilityConsumer/utilityConsumer";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

const validFileFormats = ["xls", "xlsx"];

const DebitNote = ({ t }) => {
  const [uploadDebitNoteFile, { isLoading: isDebitNoteFileUploading }] =
    usePostDebitNoteFileMutation();

  const [sendDebitNotice, { isLoading: isDebitNoticeSending }] =
    usePostSendDebitNoticeMutation();

  const [deleteDebitNote, { isLoading: isDeletingData }] =
    useDeleteDebitNoteMutation();

  const { roles } = useSelector((state) => state.Login);
  const isAdmin = useMemo(() => !roles.includes(CONSUMER), [roles]);
  const isPowerReadAdmin = roles.includes(POWER_READ_ADMIN);

  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [consumerNumber, setConsumerNumber] = useState("");
  const [query, setQuery] = useState({
    consumerNumber: "",
    searchDate: isAdmin ? moment() : null,
  });
  const [debitNoteIdForSendNotice, setDebitNoteIdForSendNotice] = useState("");
  const [editModalProps, setEditModalProps] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [deleteDebitNoteId, setDeleteDebitNoteId] = useState("");

  const sendNoticeHandler = async () => {
    try {
      await sendDebitNotice(debitNoteIdForSendNotice);
      setDebitNoteIdForSendNotice("");
    } catch (error) {}
  };

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Debit Note Number",
        field: "debitNoteNumber",
      },
      {
        title: "Consumer Name",
        field: "consumerName",
      },
      {
        title: "Tariff",
        field: "tariff",
      },
      {
        title: "Utility Address",
        renderCell: ({ utilityAddress }) => {
          const address = formatUtilityAddress(utilityAddress);

          if (address) {
            return (
              <>
                <p className="m-0">{address}, Gandhinagar-382050</p>
              </>
            );
          }

          return "--";
        },
        columnProps: { style: { width: "350px", minWidth: "350px" } },
      },
      {
        title: "Debit Note Date",
        renderCell: ({ debitNoteDate }) =>
          convertUTCDateToLocalDate(debitNoteDate).format("DD-MM-YYYY"),
      },
      {
        title: "Actions",
        renderCell: ({
          url,
          id,
          utilityConsumerId,
          billOfSupplyUrl,
          taxInvoiceUrl,
        }) => (
          <Box sx={{ minWidth: "120px" }}>
            <IconButton
              onClick={() =>
                setDocumentToPreview({
                  url: getImageUrl(url),
                  documentTypeName: "Debit Note Document",
                  documentType: "pdf",
                  documentName: "Debit Note Document",
                })
              }
            >
              <Tooltip title={t("View Debit Note Document")}>
                <RemoveRedEyeIcon fontSize="small" color="primary" />
              </Tooltip>
            </IconButton>

            {isAdmin && !isPowerReadAdmin && (
              <IconButton
                onClick={() => setEditModalProps({ utilityConsumerId, id })}
              >
                <Tooltip title={t("Edit Debit Note Details")}>
                  <EditIcon fontSize="small" color="primary" />
                </Tooltip>
              </IconButton>
            )}
            {!!billOfSupplyUrl && (
              <Tooltip title={t("Bill Of Supply")}>
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      url: getImageUrl(billOfSupplyUrl),
                      documentTypeName: "Bill Of Supply Document",
                      documentType: "pdf",
                      documentName: "Bill Of Supply",
                    });
                  }}
                  size="small"
                >
                  <FileCopyIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}

            {!!taxInvoiceUrl && (
              <Tooltip title={t("Tax Invoice")}>
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      url: getImageUrl(taxInvoiceUrl),
                      documentTypeName: "Tax Invoice Document",
                      documentType: "pdf",
                      documentName: "Tax Invoice",
                    });
                  }}
                  size="small"
                >
                  <ReceiptIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {isAdmin && !isPowerReadAdmin && (
              <IconButton onClick={() => setDebitNoteIdForSendNotice(id)}>
                <Tooltip title={t("Send Notice")}>
                  <SendIcon fontSize="small" color="primary" />
                </Tooltip>
              </IconButton>
            )}

            {isAdmin && !isPowerReadAdmin && (
              <IconButton
                onClick={() => {
                  setDeleteDebitNoteId(id);
                }}
              >
                <Tooltip title={t("Delete Debit Note Details")}>
                  <DeleteIcon fontSize="small" color="primary" />
                </Tooltip>
              </IconButton>
            )}
          </Box>
        ),
      },
    ],
    []
  );

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: isAdmin ? "/debit-note" : "/debit-note/user",
      tags: [DEBIT_NOTE],
      query: {
        ...query,
        searchDate: query.searchDate
          ? encodeURIComponent(query.searchDate.format("YYYY-MM-DD"))
          : "",
      },
    }),
    [query, isAdmin]
  );

  const handleUploadFile = async (selectedFile) => {
    try {
      const formData = new FormData();
      const fileExtension = getFileExtension(selectedFile.name)?.toLowerCase();
      if (!validFileFormats.includes(fileExtension)) {
        toast.error(
          "Invalid file format, Please upload file with .xsl or .xlsx extension."
        );
      } else {
        formData.append("file", selectedFile);

        await uploadDebitNoteFile(formData);
      }
    } catch (err) {}
  };
  return (
    <>
      <Backdrop open={isDebitNoteFileUploading} />

      {!!debitNoteIdForSendNotice && (
        <ConfirmationDialog
          message="Are you sure you want to send notice?"
          onCancel={() => setDebitNoteIdForSendNotice("")}
          onConfirm={sendNoticeHandler}
          isOpen
          title="Warning"
          variant="warning"
          isLoading={isDebitNoticeSending}
        />
      )}

      {!!deleteDebitNoteId && (
        <ConfirmationDialog
          message="Are you sure you want to delete?"
          onCancel={() => setDeleteDebitNoteId("")}
          onConfirm={async () => {
            await deleteDebitNote(deleteDebitNoteId);
            setDeleteDebitNoteId("");
          }}
          isOpen={!!deleteDebitNoteId}
          title="Warning"
          variant="warning"
          isLoading={isDeletingData}
        />
      )}

      <BreadCrumb
        title={t("Debit Note Advice")}
        pageTitle={t(isAdmin ? "Payment" : "")}
      />

      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      {(!!editModalProps || isFormModalOpen) && (
        <DebitNoteForm
          editModalProps={editModalProps}
          handleClose={() => {
            setEditModalProps(null);
            setIsFormModalOpen(false);
          }}
        />
      )}

      <Grid container columnSpacing={2} className="d-flex  flex-wrap">
        <Grid item xs={12} sm={6} md={4}>
          <DatePicker
            slotProps={{
              textField: {
                size: "small",
              },
            }}
            label={t("Select Month and Year")}
            className="w-100"
            views={["month", "year"]}
            value={query.searchDate}
            disableFuture
            onChange={(date) =>
              setQuery((prevState) => ({
                ...prevState,
                searchDate: date,
              }))
            }
          />
        </Grid>
        {isAdmin && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <InputFieldText
                label={t("Consumer Number")}
                placeholder="Enter Consumer Number"
                value={consumerNumber}
                onChange={(e) => {
                  setConsumerNumber(e.target.value);
                  debounceFn(
                    () =>
                      setQuery((prevState) => ({
                        ...prevState,
                        consumerNumber: e.target.value,
                      })),
                    300
                  );
                }}
              />
            </Grid>

            {!isPowerReadAdmin && (
              <Grid item>
                <Button
                  variant="contained"
                  component="label"
                  className="mb-2 me-2"
                  sx={{ height: "40px", fontSize: "12px" }}
                  onClick={() => setIsFormModalOpen(true)}
                >
                  {t("Add")}
                </Button>
                <Button
                  variant="contained"
                  component="label"
                  className="mb-2 me-2"
                  sx={{ height: "40px", fontSize: "12px" }}
                >
                  {t("Upload")}
                  <input
                    style={{
                      display: "none",
                    }}
                    accept=".xls, .xlsx"
                    type="file"
                    onChange={async (e) => {
                      await handleUploadFile(e.target.files[0]);
                      e.target.value = null;
                    }}
                  />
                </Button>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <BaseTable
        columns={columns}
        pageSize={10}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
      />
    </>
  );
};

DebitNote.propTypes = { t: PropTypes.func };

export default withTranslation()(DebitNote);
